import React, { useMemo, useState, useEffect } from "react";
import recipeCardStyles from '../components/RecipeCard.module.css';
import { useNavigate, Link } from "react-router-dom";
import { api } from '../api/client';
import * as dogsApi from '../api/dogs';
import type { Dog } from '../api/dogs';
import { useAuth } from '../auth/AuthContext';
import { isPremium, isAdmin } from '../utils/userAccess';
import InfoIcon from '../assets/icons/info.svg';
import DogFoodIcon from '../assets/icons/dog-food-pictogram-2-svgrepo-com.svg';
import "./RecipeBuilder.css";
import buttonStyles from '../components/Buttons.module.css';
import { BACKEND_URL } from "../config";

type Ingredient = {
  id: string;
  label: string;
  emoji?: string;
  category: string;
  comingSoon?: boolean;
};

function useSelections() {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const remove = (id: string) => setSelectedIds((prev) => prev.filter((x) => x !== id));
  const clearAll = () => setSelectedIds([]);

  return { selectedIds, setSelectedIds, toggle, remove, clearAll };
}

const IngredientChip: React.FC<{
  item: Ingredient;
  selected: boolean;
  onToggle: (id: string) => void;
}> = ({ item, selected, onToggle }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  
  return (
    <div 
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => item.comingSoon && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        className={`chip ${selected ? "chip--selected" : ""} ${item.comingSoon ? "chip--disabled" : ""}`}
        aria-pressed={selected}
        onClick={() => {
          if (!item.comingSoon) {
            onToggle(item.id);
          }
        }}
        style={{
          opacity: item.comingSoon ? 0.5 : 1,
          cursor: item.comingSoon ? 'not-allowed' : 'pointer',
          position: 'relative'
        }}
      >
        <span className="chip__emoji" aria-hidden>{item.emoji}</span>
        <span className="chip__label">{item.label}</span>
        {selected && !item.comingSoon && <span className="chip__check" aria-hidden>✓</span>}
      </button>
      {showTooltip && item.comingSoon && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '6px',
          padding: '4px 8px',
          background: '#2c3e50',
          color: '#fff',
          borderRadius: '4px',
          fontSize: '0.7rem',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          pointerEvents: 'none'
        }}>
          recipes coming soon
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid #2c3e50'
          }} />
        </div>
      )}
    </div>
  );
};

const SelectedBar: React.FC<{
  items: Ingredient[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onNext: () => void;
  canNext: boolean;
}> = ({ items, onRemove, onClear, onNext, canNext }) => (
  <div className="selectedBar" role="region" aria-label="Selected ingredients">
    <div className="selectedBar__scroller">
      {items.length === 0 ? (
        <span className="selectedBar__empty">Nothing selected yet</span>
      ) : (
        items.map((it) => (
          <button key={it.id} className={buttonStyles.pill} onClick={() => onRemove(it.id)}>
            <span className={buttonStyles.pillEmoji} aria-hidden>{it.emoji}</span>
            <span className={buttonStyles.pillLebel}>{it.label}</span>
            <span className={buttonStyles.pillX} aria-hidden>✕</span>
          </button>
        ))
      )}
    </div>
    <div className="selectedBar__actions">
      <button className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`} onClick={onClear} disabled={items.length === 0}>
        Clear All
      </button>
      <button className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`} onClick={onNext} disabled={!canNext}>
        Next →
      </button>
    </div>
  </div>
);

const CollapsibleSection: React.FC<{
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ title, children, isOpen, onToggle }) => (
  <section className="section">
    <div className="section__title" style={{ cursor: 'pointer' }} onClick={onToggle}>
      <span className="section__dot" aria-hidden></span>
      <h3>{title} <span style={{ fontSize: 14, marginLeft: 8 }}>{isOpen ? '▲' : '▼'}</span></h3>
    </div>
    {isOpen && <div className="grid">{children}</div>}
  </section>
);

const StepIndicator: React.FC<{ step: number; total: number; label?: string }> = ({ step, total, label }) => (
  <div className="steps" aria-label="Progress">
    <div className="steps__bar">
      <div className="steps__fill" style={{ width: `${(step / total) * 100}%` }} />
    </div>
    <div className="steps__label">{label ?? `Step ${step} of ${total}`}</div>
  </div>
);

const IngredientSelectorApp: React.FC = () => {
  // For fallback inspiration recipes
  const [popularRecipes, setPopularRecipes] = useState<any[]>([]);
  const navigate = useNavigate();
  // Declare selections before useEffect
  const { selectedIds, setSelectedIds, toggle, remove, clearAll } = useSelections();

  useEffect(() => {
    // Only fetch once, for fallback inspiration
    api.get('/recipes')
      .then(res => setPopularRecipes(res.data.slice(0, 5)))
      .catch(() => setPopularRecipes([]));
  }, []);

  // Match wizard: open recipe details and store dogProfile
  const openRecipeDetails = (recipe: any) => {
    // Convert weight to lbs if needed
    let weightLbs = weight;
    if (weight && weightUnit === 'kg') {
      weightLbs = (parseFloat(weight) * 2.20462).toFixed(2);
    }
    // Save all dog profile fields
    localStorage.setItem("dogProfile", JSON.stringify({
      name: dogName,
      age: age ? String(age) : "",
      weight: weightLbs,
      weight_unit: 'lb',
      meals_per_day: mealsPerDay,
      body_condition: bodyCondition,
      allergies,
    }));
    // Pass builder flag and batch info in URL
    navigate(`/recipes/${recipe.slug}?showPortions=true&fromBuilder=1&batchSize=Normal&portionMultiplier=1`);
  };
  // Results state for step 3
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  const [searchedRecipes, setSearchedRecipes] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [dailyPortionGrams, setDailyPortionGrams] = useState<string>("");

  // Restore builder results from localStorage if present
  useEffect(() => {
    const path = window.location.pathname;
    if (path.endsWith('/recipe-builder/results')) {
      const storedResults = localStorage.getItem('builderResults');
      if (storedResults) {
        try {
          const data = JSON.parse(storedResults);
          setSearchedRecipes(data.searchedRecipes || []);
          setDailyPortionGrams(data.dailyPortionGrams || "");
          setDogName(data.dogName || "");
          setAge(data.age || "");
          setWeight(data.weight || "");
          setWeightUnit(data.weightUnit || "kg");
          setMealsPerDay(data.mealsPerDay || 1);
          setBodyCondition(data.bodyCondition || "");
          setAllergies(data.allergies || "");
          if (Array.isArray(data.selectedIds) && data.selectedIds.length > 0) {
            // Restore selected ingredients directly
            setSelectedIds(data.selectedIds);
          }
        } catch {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hardcoded options for age and weight (same as wizard)
  const ageOptions = [">1", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "<15"];
  const { user } = useAuth();
  const [savedDogs, setSavedDogs] = useState<Dog[]>([]);
  const [selectedDogId, setSelectedDogId] = useState<number | null>(null);
  const [dogName, setDogName] = useState("");
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [mealsPerDay, setMealsPerDay] = useState(1);
  const [bodyCondition, setBodyCondition] = useState("");
  const [allergies, setAllergies] = useState("");
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Fetch saved dogs on mount if user is logged in
  useEffect(() => {
    if (user) {
      loadSavedDogs();
    }
  }, [user]);

  const loadSavedDogs = async () => {
    try {
      const dogs = await dogsApi.getDogs();
      setSavedDogs(dogs);
    } catch (err) {
      console.error('Failed to load saved dogs:', err);
    }
  };

  const handleSelectDog = (dogId: number) => {
    const dog = savedDogs.find(d => d.id === dogId);
    if (dog) {
      setSelectedDogId(dogId);
      setDogName(dog.name);
      setAge(dog.age?.toString() || '');
      setWeight(dog.weight?.toString() || '');
      setMealsPerDay(dog.meals_per_day || 1);
      setBodyCondition(dog.body_condition || '');
      setAllergies(dog.allergies ? dog.allergies.join(', ') : '');
    }
  };

  const handleClearSelection = () => {
    setSelectedDogId(null);
    setDogName('');
    setAge('');
    setWeight('');
    setMealsPerDay(1);
    setBodyCondition('');
    setAllergies('');
  };

  // Free user ingredients (only from the 2 free recipes)
  const freeProteins = useMemo(() => [
    { id: "chicken", label: "Chicken", category: "Proteins", emoji: "🍗" },
    { id: "beef", label: "Beef", category: "Proteins", emoji: "🥩" },
    { id: "liver", label: "Liver", category: "Proteins", emoji: "🍖" },
    { id: "chicken_eggs", label: "Chicken Eggs", category: "Proteins", emoji: "🥚" },
    { id: "sardines", label: "Sardines", category: "Proteins", emoji: "🐟" },
  ], []);

  const freeVeggies = useMemo(() => [
    { id: "carrots", label: "Carrots", category: "Veggies", emoji: "🥕" },
    { id: "green_beans", label: "Green Beans", category: "Veggies", emoji: "🫛" },
    { id: "broccoli", label: "Broccoli", category: "Veggies", emoji: "🥦" },
    { id: "spinach", label: "Spinach", category: "Veggies", emoji: "🥬" },
    { id: "pumpkin", label: "Pumpkin", category: "Veggies", emoji: "🎃" },
    { id: "sweet_potato", label: "Sweet Potato", category: "Veggies", emoji: "🍠" },
    { id: "bell_peppers", label: "Bell Peppers", category: "Veggies", emoji: "🫑" },
  ], []);

  const freeFruits = useMemo(() => [
    { id: "blueberries", label: "Blueberries", category: "Fruits", emoji: "🫐" },
  ], []);

  // Full ingredient lists for premium users
  const proteins = useMemo(() => [
    { id: "chicken", label: "Chicken", category: "Proteins", emoji: "🍗" },
    { id: "turkey", label: "Turkey", category: "Proteins", emoji: "🦃" },
    { id: "beef", label: "Beef", category: "Proteins", emoji: "🥩" },
    { id: "lamb", label: "Lamb", category: "Proteins", emoji: "🐑", comingSoon: true  },
    { id: "pork", label: "Pork", category: "Proteins", emoji: "🐖", comingSoon: true },
    { id: "venison", label: "Venison", category: "Proteins", emoji: "🦌" },
    { id: "bison", label: "Bison", category: "Proteins", emoji: "🐃" },
    { id: "buffalo", label: "Buffalo", category: "Proteins", emoji: "🐃", comingSoon: true },
    { id: "goat", label: "Goat", category: "Proteins", emoji: "🐐", comingSoon: true },
    { id: "rabbit", label: "Rabbit", category: "Proteins", emoji: "🐇", comingSoon: true },
    { id: "duck", label: "Duck", category: "Proteins", emoji: "🦆", comingSoon: true },
    { id: "goose", label: "Goose", category: "Proteins", emoji: "🪿", comingSoon: true },
    { id: "horse", label: "Horse Meat", category: "Proteins", emoji: "🐴" },
    { id: "kangaroo", label: "Kangaroo", category: "Proteins", emoji: "🦘" },
    { id: "salmon", label: "Salmon", category: "Proteins", emoji: "🐟" },
    { id: "sardines", label: "Sardines", category: "Proteins", emoji: "🐟" },
    { id: "mackerel", label: "Mackerel", category: "Proteins", emoji: "🐟", comingSoon: true },
    { id: "herring", label: "Herring", category: "Proteins", emoji: "🐟", comingSoon: true  },
    { id: "cod", label: "Cod", category: "Proteins", emoji: "🐟" },
    { id: "tilapia", label: "Tilapia", category: "Proteins", emoji: "🐟" },
    { id: "whitefish", label: "Whitefish", category: "Proteins", emoji: "🐟" },
    { id: "trout", label: "Trout", category: "Proteins", emoji: "🐟", comingSoon: true },
    { id: "chicken_eggs", label: "Chicken Eggs", category: "Proteins", emoji: "🥚" },
    { id: "duck_eggs", label: "Duck Eggs", category: "Proteins", emoji: "🥚", comingSoon: true },
    { id: "quail_eggs", label: "Quail Eggs", category: "Proteins", emoji: "🥚", comingSoon: true },
    { id: "liver", label: "Liver", category: "Proteins", emoji: "🍖" },
    { id: "heart", label: "Heart", category: "Proteins", emoji: "🍖", comingSoon: true },
    { id: "kidney", label: "Kidney", category: "Proteins", emoji: "🍖" },
    { id: "gizzard", label: "Gizzard", category: "Proteins", emoji: "🍖", comingSoon: true },
    { id: "tripe", label: "Tripe", category: "Proteins", emoji: "🍖", comingSoon: true },
    { id: "bone_broth", label: "Bone Broth", category: "Proteins", emoji: "🍲" },
    { id: "beef_broth", label: "Beef Broth", category: "Proteins", emoji: "🍲", comingSoon: true },
    { id: "chicken_broth", label: "Chicken Broth", category: "Proteins", emoji: "🍲", comingSoon: true },
    { id: "lentils", label: "Lentils", category: "Proteins", emoji: "🌱", comingSoon: true },
    { id: "chickpeas", label: "Chickpeas", category: "Proteins", emoji: "🌱", comingSoon: true },
    { id: "kidney_beans", label: "Kidney Beans", category: "Proteins", emoji: "🫘"},
    { id: "quinoa", label: "Quinoa", category: "Proteins", emoji: "🌾", comingSoon: true },
    { id: "oats", label: "Oats", category: "Proteins", emoji: "🌾" },
    { id: "white_rice", label: "White Rice", category: "Proteins", emoji: "🍚" },
    { id: "brown_rice", label: "Brown Rice", category: "Proteins", emoji: "🍚" },
    { id: "greek_yogurt", label: "Greek Yogurt", category: "Proteins", emoji: "🥛", comingSoon: true },
    { id: "cottage_cheese", label: "Cottage Cheese", category: "Proteins", emoji: "🧀", comingSoon: true }
  ], []);

  const veggies = useMemo(() => [
    { id: "carrots", label: "Carrots", category: "Veggies", emoji: "🥕" },
    { id: "green_beans", label: "Green Beans", category: "Veggies", emoji: "🫛" },
    { id: "broccoli", label: "Broccoli", category: "Veggies", emoji: "🥦" },
    { id: "cauliflower", label: "Cauliflower", category: "Veggies", emoji: "🥦", comingSoon: true },
    { id: "spinach", label: "Spinach", category: "Veggies", emoji: "🥬" },
    { id: "kale", label: "Kale", category: "Veggies", emoji: "🥬" },
    { id: "pumpkin", label: "Pumpkin", category: "Veggies", emoji: "🎃" },
    { id: "butternut_squash", label: "Butternut Squash", category: "Veggies", emoji: "🎃", comingSoon: true  },
    { id: "zucchini", label: "Zucchini", category: "Veggies", emoji: "🥒" },
    { id: "cucumber", label: "Cucumber", category: "Veggies", emoji: "🥒", comingSoon: true },
    { id: "sweet_potato", label: "Sweet Potato", category: "Veggies", emoji: "🍠" },
    { id: "potato_cooked_plain", label: "Potatos", category: "Veggies", emoji: "🥔", comingSoon: true },
    { id: "peas", label: "Peas", category: "Veggies", emoji: "🫛" },
    { id: "brussels_sprouts", label: "Brussels Sprouts", category: "Veggies", emoji: "🥦", comingSoon: true },
    { id: "celery", label: "Celery", category: "Veggies", emoji: "🥬", comingSoon: true },
    { id: "beets", label: "Beets", category: "Veggies", emoji: "🧃", comingSoon: true },
    { id: "parsnips", label: "Parsnips", category: "Veggies", emoji: "🥕", comingSoon: true },
    { id: "turnips", label: "Turnips", category: "Veggies", emoji: "🥔", comingSoon: true },
    { id: "lettuce", label: "Lettuce", category: "Veggies", emoji: "🥬", comingSoon: true },
    { id: "cabbage", label: "Cabbage", category: "Veggies", emoji: "🥬", comingSoon: true },
    { id: "bok_choy", label: "Bok Choy", category: "Veggies", emoji: "🥬", comingSoon: true },
    { id: "bell_peppers", label: "Bell Peppers", category: "Veggies", emoji: "🫑" },
    { id: "asparagus", label: "Asparagus", category: "Veggies", emoji: "🥦", comingSoon: true },
    { id: "okra", label: "Okra", category: "Veggies", emoji: "🌱", comingSoon: true },
    { id: "nori_seaweed_plain", label: "Nori Seaweed", category: "Veggies", emoji: "🌊", comingSoon: true },
    { id: "edamame", label: "Edamame", category: "Veggies", emoji: "🌱", comingSoon: true },
    { id: "chard", label: "Chard", category: "Veggies", emoji: "🥬", comingSoon: true },
    { id: "collard_greens", label: "Collard Greens", category: "Veggies", emoji: "🥬", comingSoon: true }
  ], []);

  const fruits = useMemo(() => [
    { id: "apple", label: "Apple", category: "Fruits", emoji: "🍎", comingSoon: true },
    { id: "bananas", label: "Bananas", category: "Fruits", emoji: "🍌", comingSoon: true },
    { id: "blueberries", label: "Blueberries", category: "Fruits", emoji: "🫐" },
    { id: "strawberries", label: "Strawberries", category: "Fruits", emoji: "🍓", comingSoon: true },
    { id: "raspberries", label: "Raspberries", category: "Fruits", emoji: "🍓", comingSoon: true },
    { id: "blackberries", label: "Blackberries", category: "Fruits", emoji: "🍇", comingSoon: true },
    { id: "watermelon", label: "Watermelon", category: "Fruits", emoji: "🍉", comingSoon: true },
    { id: "cantaloupe", label: "Cantaloupe", category: "Fruits", emoji: "🍈", comingSoon: true },
    { id: "honeydew", label: "Honeydew", category: "Fruits", emoji: "🍈", comingSoon: true },
    { id: "pineapple", label: "Pineapple", category: "Fruits", emoji: "🍍", comingSoon: true },
    { id: "mango", label: "Mango", category: "Fruits", emoji: "🥭", comingSoon: true },
    { id: "pear", label: "Pear", category: "Fruits", emoji: "🍐", comingSoon: true },
    { id: "peaches", label: "Peaches", category: "Fruits", emoji: "🍑", comingSoon: true },
    { id: "plum", label: "Plum", category: "Fruits", emoji: "🍑", comingSoon: true },
    { id: "kiwi", label: "Kiwi", category: "Fruits", emoji: "🥝", comingSoon: true },
    { id: "cranberries", label: "Cranberries", category: "Fruits", emoji: "🍒" },
    { id: "lingonberries", label: "Lingonberries", category: "Fruits", emoji: "🍒" },
    { id: "wild_blueberries", label: "Wild Blueberries", category: "Fruits", emoji: "🫐" },
    { id: "coconut", label: "Coconut", category: "Fruits", emoji: "🥥", comingSoon: true },
    { id: "papaya", label: "Papaya", category: "Fruits", emoji: "🥭", comingSoon: true },
    { id: "date", label: "Date", category: "Fruits", emoji: "🌴", comingSoon: true },
    { id: "figs", label: "Figs", category: "Fruits", emoji: "🍈", comingSoon: true },
    { id: "goji_berries", label: "Goji Berries", category: "Fruits", emoji: "🍒", comingSoon: true }
  ], []);

  // Use free or full ingredients based on user status
  const displayProteins = useMemo(() => {
    return (isPremium(user) || isAdmin(user)) ? proteins : freeProteins;
  }, [user, proteins, freeProteins]);

  const displayVeggies = useMemo(() => {
    return (isPremium(user) || isAdmin(user)) ? veggies : freeVeggies;
  }, [user, veggies, freeVeggies]);

  const displayFruits = useMemo(() => {
    return (isPremium(user) || isAdmin(user)) ? fruits : freeFruits;
  }, [user, fruits, freeFruits]);

  const ingredients = useMemo(() => [...displayProteins, ...displayVeggies, ...displayFruits], [displayProteins, displayVeggies, displayFruits]);
  const categories = useMemo(() => ["Proteins", "Veggies", "Fruits"], []);

  const selectedItems = useMemo(
    () => ingredients.filter((i) => selectedIds.includes(i.id)),
    [selectedIds, ingredients]
  );

  // Collapsible state for ingredient sections
  const [openSections, setOpenSections] = useState([true, false, false]);

    // Add state for weight unit - default to lb
  const [weightUnit, setWeightUnit] = React.useState<'kg' | 'lb'>("lb");

  const categorized = useMemo(() => {
    const map: Record<string, Ingredient[]> = {};
    for (const c of categories) map[c] = [];
    ingredients.forEach((i) => map[i.category].push(i));
    return map;
  }, [ingredients, categories]);

  const handleNext = async () => {
    if (step === 1) {
      setStep(2);
      navigate('/recipe-builder/details');
    }
    else if (step === 2) {
      setStep(3);
      navigate('/recipe-builder/results');
      setLoading(true);
      // setError(null);
      setSearching(true);
      try {
        // Split allergies by comma or space, filter out empty strings
        const allergyTerms = allergies
          .split(/[ ,]+/)
          .map(s => s.trim())
          .filter(Boolean);
        
        // Convert weight to lbs if needed (backend expects lbs)
        let weightLbs = weight;
        if (weight && weightUnit === 'kg') {
          weightLbs = (parseFloat(weight) * 2.20462).toFixed(2);
        }
        
        //  pretty sure we dont need to send all these params only allergies and selected ingredients
        const response = await api.post("/recipes/fridge_search", {
          ingredients: selectedItems.map(i => i.label),
          age,
          weight: weightLbs,
          meals_per_day: mealsPerDay,
          body_condition: bodyCondition,
          allergies: allergyTerms.length > 0 ? allergyTerms : undefined,
        });
        if (response.data.recipes?.length === 0) {
          // setError("No recipes found with the selected ingredients.");
        }
        setDailyPortionGrams(response.data.recommended_daily_portion_grams || "");
        setSearchedRecipes(response.data.recipes || []);

        // Save builder results and search params to localStorage (weight in lbs)
        localStorage.setItem('builderResults', JSON.stringify({
          searchedRecipes: response.data.recipes || [],
          dailyPortionGrams: response.data.recommended_daily_portion_grams || "",
          dogName,
          age,
          weight: weightLbs,
          weightUnit: 'lb',
          mealsPerDay,
          bodyCondition,
          allergies,
          selectedIds,
        }));
      } catch {
        // setError("An error occurred while searching for recipes.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Sync step with URL on mount and on location change
  useEffect(() => {
    const path = window.location.pathname;
    if (path.endsWith('/recipe-builder/results')) {
      setStep(3);
      if (path !== '/recipe-builder/results') navigate('/recipe-builder/results', { replace: true });
    } else if (path.endsWith('/recipe-builder/details')) {
      setStep(2);
      if (path !== '/recipe-builder/details') navigate('/recipe-builder/details', { replace: true });
    } else if (path.endsWith('/recipe-builder') || path.endsWith('/recipe-builder/')) {
      setStep(1);
      if (path !== '/recipe-builder') navigate('/recipe-builder', { replace: true });
    } else {
      // If not on a builder path, redirect to step 1
      setStep(1);
      navigate('/recipe-builder', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app">
      {step === 1 && (
        <main className="container" aria-labelledby="page-title">
          <header className="header">
            <h1 id="page-title">Select Ingredients</h1>
            <p className="sub">Tap what you have today. We'll tailor recipes to your dog.</p>
          </header>

          <StepIndicator step={1} total={3} label="Step 1 of 3 · Ingredients" />

          {user && !isPremium(user) && !isAdmin(user) && (
            <div style={{ 
              background: '#f5f5f5', 
              border: '1px solid #d4d4d4', 
              borderRadius: '8px', 
              padding: '12px 16px', 
              marginTop: '16px',
              marginBottom: '16px',
              fontSize: '0.9rem',
              color: '#4a4a4a',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <img src={InfoIcon} alt="" style={{ width: '18px', height: '18px', flexShrink: 0, filter: 'invert(50%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(90%)' }} />
              <span>Showing ingredients for your available recipes. <Link to="/pricing" style={{ color: '#c14444', textDecoration: 'underline' }}>Upgrade to premium</Link> to search across the full recipe library.</span>
            </div>
          )}

          {categories.map((cat, idx) => (
            <CollapsibleSection
              key={cat}
              title={cat}
              isOpen={openSections[idx]}
              onToggle={() => {
                setOpenSections((prev) => prev.map((open, i) => i === idx ? !open : open));
              }}
            >
              {categorized[cat]?.map((item) => (
                <IngredientChip
                  key={item.id}
                  item={item}
                  selected={selectedIds.includes(item.id)}
                  onToggle={toggle}
                />
              ))}
            </CollapsibleSection>
          ))}

          <SelectedBar
            items={selectedItems}
            onRemove={remove}
            onClear={clearAll}
            onNext={handleNext}
            canNext={selectedItems.length > 0}
          />
        </main>
      )}

      {step === 2 && (
        <main className="container">
          <header className="header">
            <h1>About Your Dog</h1>
            <p className="sub">A few details to personalize recipes.</p>
          </header>
          <StepIndicator step={2} total={3} label="Step 2 of 3 · Dog Details" />

          <form className="form" onSubmit={e => { e.preventDefault(); handleNext(); }}>
            {/* Saved Dogs Selector */}
            {user && (isPremium(user) || isAdmin(user)) && savedDogs.length > 0 && (
              <div className="saved-dogs-selector">
                {!selectedDogId ? (
                  <div className="field">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M239.71,125l-16.42-88a16,16,0,0,0-19.61-12.58l-.31.09L150.85,40h-45.7L52.63,24.56l-.31-.09A16,16,0,0,0,32.71,37.05L16.29,125a15.77,15.77,0,0,0,9.12,17.52A16.26,16.26,0,0,0,32.12,144,15.48,15.48,0,0,0,40,141.84V184a40,40,0,0,0,40,40h96a40,40,0,0,0,40-40V141.85a15.5,15.5,0,0,0,7.87,2.16,16.31,16.31,0,0,0,6.72-1.47A15.77,15.77,0,0,0,239.71,125ZM32,128h0L48.43,40,90.5,52.37Zm144,80H136V195.31l13.66-13.65a8,8,0,0,0-11.32-11.32L128,180.69l-10.34-10.35a8,8,0,0,0-11.32,11.32L120,195.31V208H80a24,24,0,0,1-24-24V123.11L107.92,56h40.15L200,123.11V184A24,24,0,0,1,176,208Zm48-80L165.5,52.37,207.57,40,224,128ZM104,140a12,12,0,1,1-12-12A12,12,0,0,1,104,140Zm72,0a12,12,0,1,1-12-12A12,12,0,0,1,176,140Z"></path>
                      </svg>
                      Use a saved dog profile
                    </span>
                    <small style={{ color: '#666', fontSize: '0.85rem', fontWeight: 'normal', display: 'block', marginTop: '4px', marginBottom: '8px' }}>
                      Auto-fills this form instantly
                    </small>
                    <select 
                      value={selectedDogId || ''} 
                      onChange={(e) => {
                        const dogId = e.target.value ? parseInt(e.target.value) : null;
                        if (dogId) {
                          handleSelectDog(dogId);
                        } else {
                          handleClearSelection();
                        }
                      }}
                    >
                      <option value="">Select your dog</option>
                      {savedDogs.map(dog => (
                        <option key={dog.id} value={dog.id}>
                          {dog.name} ({dog.age} years, {dog.weight} lbs)
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '1rem', color: '#15803d', fontWeight: 600 }}>
                      {savedDogs.find(d => d.id === selectedDogId)?.name} ({savedDogs.find(d => d.id === selectedDogId)?.age} yrs, {savedDogs.find(d => d.id === selectedDogId)?.weight} lbs) ✓
                    </span>
                    <button
                      type="button"
                      onClick={handleClearSelection}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#15803d',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        textDecoration: 'underline',
                        padding: 0
                      }}
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>
            )}

            <label className="field">
              <span>Dog’s Name</span>
              <input type="text" placeholder="Maggie" value={dogName} onChange={e => setDogName(e.target.value)} required />
            </label>
            <label className="field">
              <span>Age (years)</span>
              <select value={age} onChange={e => setAge(e.target.value)} required>
                <option value="" disabled>Select age</option>
                {ageOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Weight</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="number"
                  min={0.5}
                  step={0.1}
                  value={weight}
                  onChange={e => {
                    const val = e.target.value;
                    if (/^\d*\.?\d*$/.test(val)) setWeight(val);
                  }}
                  style={{ width: 100, fontSize: '1rem', borderRadius: 6, border: '1px solid #c14444', padding: 6 }}
                  required
                />
                <span style={{ fontWeight: 600, color: '#c14444', fontSize: '1.08rem', marginLeft: 6 }}>{weightUnit}</span>
                <button
                  type="button"
                  style={{
                    marginLeft: 4,
                    padding: '2px 14px',
                    borderRadius: 6,
                    border: '1px solid #c14444',
                    background: weightUnit === 'kg' ? '#ffe5c2' : '#fff',
                    color: weightUnit === 'kg' ? '#c14444' : '#888',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                  onClick={() => setWeightUnit('kg')}
                  aria-pressed={weightUnit === 'kg'}
                >
                  kg
                </button>
                <button
                  type="button"
                  style={{
                    marginLeft: 0,
                    padding: '2px 14px',
                    borderRadius: 6,
                    border: '1px solid #c14444',
                    background: weightUnit === 'lb' ? '#ffe5c2' : '#fff',
                    color: weightUnit === 'lb' ? '#c14444' : '#888',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                  onClick={() => setWeightUnit('lb')}
                  aria-pressed={weightUnit === 'lb'}
                >
                  lb
                </button>
              </div>
              <div style={{ marginTop: 4, color: '#223366', fontSize: '0.98rem', fontWeight: 500 }}>
                {weight && weightUnit === 'kg'
                  ? `${(parseFloat(weight) * 2.20462).toFixed(2)} lb`
                  : weight && weightUnit === 'lb'
                  ? `${(parseFloat(weight) / 2.20462).toFixed(2)} kg`
                  : ''}
              </div>
              {weight && parseFloat(weight) < 0.5 && (
                <div style={{ color: 'red', fontWeight: 600, marginTop: 4, fontSize: '0.98rem' }}>
                  Weight must be at least 0.5 {weightUnit}
                </div>
              )}
              {weight && ((weightUnit === 'kg' && parseFloat(weight) > 150) || (weightUnit === 'lb' && parseFloat(weight) > 330)) && (
                <div style={{ color: 'red', fontWeight: 600, marginTop: 4, fontSize: '0.98rem' }}>
                  Weight cannot exceed {weightUnit === 'kg' ? '150 kg' : '330 lb'}
                </div>
              )}
            </label>
            <label className="field">
              <span>Body Condition</span>
              <select value={bodyCondition} onChange={e => setBodyCondition(e.target.value)} required>
                <option value="" disabled>Select body condition</option>
                <option value="too thin">Too Thin</option>
                <option value="ideal">Ideal</option>
                <option value="too heavy">Too Heavy</option>
              </select>
            </label>
            <label className="field">
              <span>Meals per day</span>
              <select value={mealsPerDay} onChange={e => setMealsPerDay(Number(e.target.value))} required>
                {[1,2,3,4].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Allergies (optional)</span>
              <input
                type="text"
                placeholder="e.g., chicken, wheat"
                value={allergies}
                onChange={e => setAllergies(e.target.value)}
              />
            </label>

            <div className="actions">
              <button type="button" className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`} onClick={() => setStep(1)}>
                ← Back
              </button>
              <button
                type="submit"
                className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                disabled={!age || !weight || !mealsPerDay || !bodyCondition || parseFloat(weight) < 0.5 || (weightUnit === 'kg' && parseFloat(weight) > 150) || (weightUnit === 'lb' && parseFloat(weight) > 330)}
              >
                Next →
              </button>
            </div>
          </form>
        </main>
      )}

      {step === 3 && (
        <main className="container">
          <header className="header">
            <h1>Your Custom Recipes</h1>
            <p className="sub">Generated from your selected ingredients.</p>
          </header>
          <StepIndicator step={3} total={3} label="Step 3 of 3 · Results" />

          {/* Free user calculations remaining */}
          {user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining !== null && user.calculations_remaining !== undefined && (
            <div style={{ 
              textAlign: 'center', 
              padding: '12px 16px', 
              background: user.calculations_remaining === 0 ? '#fff5f0' : '#f5f5f5',
              border: `1px solid ${user.calculations_remaining === 0 ? '#ffe5c2' : '#d4d4d4'}`,
              borderRadius: '8px',
              marginTop: '16px',
              marginBottom: '16px',
              fontSize: '0.95rem',
              color: '#4a4a4a',
              maxWidth: '600px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              {user.calculations_remaining === 0 ? (
                <>
                  You've reached your free calculation limit.
                  <br></br>
                  <Link to="/pricing" style={{ color: '#c14444', textDecoration: 'underline', fontWeight: 600 }}>Upgrade</Link> for unlimited portions <img src={DogFoodIcon} alt="" style={{ width: '18px', height: '18px', verticalAlign: 'middle', marginLeft: '4px', filter: 'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(84%) contrast(97%)' }} />
                  <br></br>
                  You can still browse recipes anytime.
                </>
              ) : (
                <>{user.calculations_remaining} of 3 free calculation{user.calculations_remaining !== 1 ? 's' : ''} remaining</>
              )}
            </div>
          )}
          {user && !isPremium(user) && !isAdmin(user) && user.calculations_remaining === 1 && (
            <div style={{ 
              textAlign: 'center', 
              fontSize: '0.85rem',
              color: '#666',
              marginTop: '-8px',
              marginBottom: '8px'
            }}>
              Tired of re-entering your dog's details? <Link to="/pricing" style={{ color: '#c14444', textDecoration: 'underline' }}>Save a profile with premium</Link>
            </div>
          )}

          {/* Show selected ingredients for reference */}
          {selectedItems.length > 0 && (
            <section style={{ margin: '1.5rem 0', padding: '1rem', background: '#fffbe6', borderRadius: 10, boxShadow: '0 1px 4px rgba(44,62,80,0.07)', maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
              <h3 style={{ fontSize: 18, color: '#c14444', marginBottom: 8, fontWeight: 600 }}>Ingredients You Searched For</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {selectedItems.map(item => (
                  <span key={item.id} style={{ display: 'inline-flex', alignItems: 'center', background: '#ffe5c2', color: '#c14444', fontWeight: 600, fontSize: 15, borderRadius: 16, padding: '4px 14px', marginBottom: 4 }}>
                    <span style={{ marginRight: 6 }}>{item.emoji}</span> {item.label}
                  </span>
                ))}
              </div>
            </section>
          )}

          {loading && <div className="loading-spinner">Loading...</div>}
          {searching && !loading && searchedRecipes.length === 0 && (
            <div className="empty-state-fallback" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 auto', padding: '40px 24px 32px 24px' }}>
              <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 12, color: '#232323', textAlign: 'center' }}>We couldn’t match those ingredients.</h2>
              <p style={{ color: '#555', fontSize: 16, marginBottom: 28, textAlign: 'center', maxWidth: 420 }}>
                Start over and change your ingredients or explore popular recipes below.
              </p>
              <div className="actions actions--end" style={{ marginBottom: 32 }}>
                <button className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`} onClick={() => { setStep(1); navigate('/recipe-builder'); }} style={{ fontSize: 15, padding: '10px 28px' }}>Start Over</button>
              </div>
              {popularRecipes.length > 0 && (
                <section className="popular-fallback" style={{ width: '100%', marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 4 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 600, color: '#232323', margin: 0 }}>Popular Recipes</h3>
                    <span style={{ flex: 1, height: 1, background: '#ececec', borderRadius: 1, marginLeft: 8 }}></span>
                  </div>
                  <div style={{ textAlign: 'center', color: '#888', fontSize: 13, marginBottom: 20 }}>Based on what our users cook most</div>
                  <div className={recipeCardStyles.recipeGrid}>
                    {popularRecipes.map((recipe) => (
                      <div
                        className={recipeCardStyles.recipeCard}
                        key={recipe.id}
                        onClick={() => openRecipeDetails(recipe)}
                      >
                        <div>
                          <img src={`${BACKEND_URL}${recipe.photo_url}`} alt={recipe.title} className={recipeCardStyles.recipeImage} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        </div>
                        <div className={recipeCardStyles.recipeTextContainer}>
                          <h4 className={recipeCardStyles.recipeTitle}>{recipe.title}</h4>
                          {recipe.description && (
                            <div className={recipeCardStyles.recipeDescription}>{recipe.description}</div>
                          )}
                        </div>
                        <div>
                          <button
                            className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                            onClick={e => { e.stopPropagation(); openRecipeDetails(recipe); }}
                          >
                            View Recipe
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
          {searchedRecipes.length > 0 && (
            <>
              {dailyPortionGrams && (
                <div style={{ marginBottom: "1.5rem", fontWeight: 600, color: "#b24646" }}>
                  Recommended daily portion: {dailyPortionGrams}g
                </div>
              )}
              <div className={recipeCardStyles.recipeGrid}>
                {searchedRecipes.map((recipe: any) => (
                  <div className={recipeCardStyles.recipeCard} key={recipe.id}>
                    <div>
                      <img src={`${BACKEND_URL}${recipe.photo_url}`} alt={recipe.title} className={recipeCardStyles.recipeImage} />
                    </div>
                    <div className={recipeCardStyles.recipeTextContainer}>
                      <h4 className={recipeCardStyles.recipeTitle}>{recipe.title}</h4>
                      {recipe.description && (
                        <div className={recipeCardStyles.recipeDescription}>{recipe.description}</div>
                      )}
                    </div>
                    <div>
                      <button
                        className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                        onClick={() => openRecipeDetails(recipe)}
                      >
                        Open recipe
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {searchedRecipes.length > 0 && (
            <>
              <div style={{ 
                background: 'linear-gradient(135deg, #fff7f0 0%, #ffe8d6 100%)', 
                border: '2px solid #d97706', 
                borderRadius: 16, 
                padding: '1.5rem 2rem',
                margin: '2rem auto',
                maxWidth: 600,
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#b24646', fontSize: '1.2rem', marginTop: 0, marginBottom: '0.8rem', fontWeight: 700 }}>New to Homemade Dog Food?</h3>
                <p style={{ margin: '0 0 1rem 0', color: '#555', fontSize: '1rem' }}>
                  Learn how to safely transition your dog to homemade meals with our step-by-step guide.
                </p>
                <Link 
                  to="/transition-guide" 
                  className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                >
                  View Transition Guide
                </Link>
              </div>
              <div className="actions actions--end">
                <button className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`} onClick={() => {
                  setStep(1);
                  navigate('/recipe-builder');
                  // Clear builder results from localStorage when starting over
                  localStorage.removeItem('builderResults');
                }}>Start Over</button>
              </div>
            </>
          )}
        </main>
      )}
    </div>
  );
};

export default IngredientSelectorApp;